import type { ComponentProps } from 'react';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';

interface Props extends ComponentProps<'button'> {
  variant?: Variant;
  /** xs is the confirm bar's 30px pill (mockups' .btn.xs). */
  size?: 'xs' | 'sm' | 'md';
  glow?: boolean;
}

export default function Button({
  variant = 'primary',
  size = 'md',
  glow = false,
  className = '',
  children,
  ...rest
}: Props) {
  const sizeStyles = size === 'xs'
    ? 'min-h-[30px] px-3 py-1 text-[12px]'
    : size === 'sm' ? 'px-3 py-1.5 text-[13px]' : 'px-5 py-3 text-[14px]';

  let variantStyles = '';
  if (variant === 'primary') {
    variantStyles =
      'bg-accent text-accent-fg border border-accent ' +
      'hover:bg-accent-hover active:bg-accent-pressed hover:-translate-y-px ' +
      (glow ? 'hover:shadow-flare ' : '');
  } else if (variant === 'secondary') {
    variantStyles =
      'bg-transparent text-fg border border-line ' +
      'hover:border-fg/60 hover:bg-elevated';
  } else if (variant === 'outline') {
    // The mockups' teal outline pill: accent text and border, fills on hover.
    variantStyles =
      'bg-transparent text-accent border border-accent ' +
      'hover:bg-accent hover:text-accent-fg';
  } else if (variant === 'danger') {
    variantStyles =
      'bg-transparent text-muted border border-line ' +
      'hover:text-accent hover:border-accent';
  } else {
    variantStyles = 'bg-transparent text-muted border border-transparent hover:text-fg';
  }

  return (
    <button
      className={
        `inline-flex items-center justify-center gap-2 rounded-full font-semibold ` +
        `transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ` +
        `disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 ` +
        `focus:outline-none focus-visible:shadow-focus ` +
        `${sizeStyles} ${variantStyles} ${className}`
      }
      {...rest}
    >
      {children}
    </button>
  );
}
